import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { V2ProductRegulationSectionContext } from './context'
import type {
  V2ProductRegulationSectionContextValue,
  V2ProductRegulationSectionProps,
} from './types'
import { V2ProductRegulationSectionApplicableContent } from './V2ProductRegulationSectionApplicableContent'
import { V2ProductRegulationSectionHeader } from './V2ProductRegulationSectionHeader'
import { V2ProductRegulationSectionNotApplicableEmpty } from './V2ProductRegulationSectionNotApplicableEmpty'
import { V2ProductRegulationSectionPendingQuestionsEmpty } from './V2ProductRegulationSectionPendingQuestionsEmpty'
import { V2ProductRegulationSectionSheets } from './V2ProductRegulationSectionSheets'
import { V2ProductRegulationSectionUndecidedEmpty } from './V2ProductRegulationSectionUndecidedEmpty'

export function V2ProductRegulationSection({
  productId,
  law,
  applicability,
  isLoading,
  showRunningOverlay,
  actionsDisabled,
  isLawDiscoverPending = false,
  onRunLawDiscover,
  children,
}: V2ProductRegulationSectionProps) {
  const { t } = useTranslation('common')
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false)
  const [determineSheetOpen, setDetermineSheetOpen] = useState(false)
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false)

  const lawLabel = t(`v2.applicability.laws.${law}`)
  const showRunStateLabel =
    showRunningOverlay || applicability?.runState === 'running'
  const questionnaireEditDisabled =
    actionsDisabled ||
    showRunStateLabel ||
    applicability?.source === 'USER_FORCE'

  function openDetermineSheet() {
    setDetermineSheetOpen(true)
  }

  function handleRunTest() {
    setDetermineSheetOpen(false)
    onRunLawDiscover?.(law)
  }

  function handleAnswerManually() {
    setDetermineSheetOpen(false)
    setQuestionnaireOpen(true)
  }

  function openQuestionnaire() {
    setQuestionnaireOpen(true)
  }

  function openOverrideDialog() {
    setOverrideDialogOpen(true)
  }

  const contextValue = useMemo(
    (): V2ProductRegulationSectionContextValue => ({
      productId,
      law,
      lawLabel,
      applicability,
      isLoading,
      showRunningOverlay,
      actionsDisabled,
      questionnaireEditDisabled,
      isLawDiscoverPending,
      onRunLawDiscover,
      openDetermineSheet,
      openQuestionnaire,
      openOverrideDialog,
      determineSheetOpen,
      setDetermineSheetOpen,
      questionnaireOpen,
      setQuestionnaireOpen,
      overrideDialogOpen,
      setOverrideDialogOpen,
      handleRunTest,
      handleAnswerManually,
    }),
    [
      productId,
      law,
      lawLabel,
      applicability,
      isLoading,
      showRunningOverlay,
      actionsDisabled,
      questionnaireEditDisabled,
      isLawDiscoverPending,
      onRunLawDiscover,
      determineSheetOpen,
      questionnaireOpen,
      overrideDialogOpen,
    ]
  )

  return (
    <V2ProductRegulationSectionContext.Provider value={contextValue}>
      <div className='space-y-6'>
        <header className='space-y-3'>
          <V2ProductRegulationSectionHeader />
        </header>
        <V2ProductRegulationSectionApplicableContent>
          {children}
        </V2ProductRegulationSectionApplicableContent>
        <V2ProductRegulationSectionPendingQuestionsEmpty />
        <V2ProductRegulationSectionNotApplicableEmpty />
        <V2ProductRegulationSectionUndecidedEmpty />
        <V2ProductRegulationSectionSheets />
      </div>
    </V2ProductRegulationSectionContext.Provider>
  )
}
